/**
 * Monitor event system for cross-component communication
 * Allows chat actions to notify MonitorButton and other components
 * when monitors are created, updated, or deleted
 */

export type MonitorEventType = 'monitor:created' | 'monitor:updated' | 'monitor:deleted';

export interface MonitorEventDetail {
  monitorId?: string;
  tableId?: string;
}

/**
 * Dispatch a monitor change event
 */
export function dispatchMonitorEvent(
  type: MonitorEventType,
  detail?: MonitorEventDetail
): void {
  const event = new CustomEvent(type, { detail });
  window.dispatchEvent(event);
}

/**
 * Subscribe to monitor change events
 * Returns cleanup function to remove listener
 */
export function subscribeToMonitorEvents(
  callback: (type: MonitorEventType, detail?: MonitorEventDetail) => void
): () => void {
  const types: MonitorEventType[] = ['monitor:created', 'monitor:updated', 'monitor:deleted'];

  const handlers = types.map(type => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<MonitorEventDetail>;
      callback(type, customEvent.detail);
    };
    window.addEventListener(type, handler);
    return { type, handler };
  });

  // Return cleanup function
  return () => {
    handlers.forEach(({ type, handler }) => {
      window.removeEventListener(type, handler);
    });
  };
}
